export type Price = {
  asNumber: number;
  asFormattedString: string;
  currency: 'USD';
  savings: null | string;
};

export enum SubscriptionTitleEnum {
  Privee = 'Privee',
  Premier = 'Premier',
  Classique = 'Classique',
  'Classique+' = 'Classique+',
  Couture = 'Couture',
  'Couture+' = 'Couture+'
}

export type SubscriptionTitle = `${SubscriptionTitleEnum}`;

export const FallbackSubscriptionPrices: Record<
  SubscriptionTitle | 'PriveeInitiationFee',
  Price
> = {
  Privee: {
    asNumber: 80000,
    asFormattedString: '$800.00',
    currency: 'USD',
    savings: null
  },
  PriveeInitiationFee: {
    asNumber: 10000,
    asFormattedString: '$1,000.00',
    currency: 'USD',
    savings: null
  },
  Premier: {
    asNumber: 4900,
    asFormattedString: '$49.00',
    currency: 'USD',
    savings: null
  },
  Classique: {
    asNumber: 13900,
    asFormattedString: '$139.00',
    currency: 'USD',
    savings: null
  },
  'Classique+': {
    asNumber: 24900,
    asFormattedString: '$249.00',
    currency: 'USD',
    savings: null
  },
  Couture: {
    asNumber: 23900,
    asFormattedString: '$239.00',
    currency: 'USD',
    savings: null
  },
  'Couture+': {
    asNumber: 33900,
    asFormattedString: '$339.00',
    currency: 'USD',
    savings: null
  }
};

export type AddOnType = 'premier' | 'none';
export type InsuranceType = 'standard' | 'premium' | 'vip';

export type ClosetTier = 'Premier' | 'Classique' | 'Couture' | 'Privee';

export type SubscriptionProduct<
  T extends SubscriptionTitle = SubscriptionTitle
> = {
  /** EPCC product ID */
  id: string;

  /** Human Readable name for the Subscription Product */
  name: T;

  /** Dev friendly name. Can be used as a className and the sort */
  slug: string;

  /** Shows pricing for each pre-pay group/period */
  price: Price;

  /** ID of each EPCC product representing the given pre-pay period / plan */
  productId: string;

  /** Determines how many items a user can borrow at one time */
  allowed_borrowed_items: number;

  /** Render this via `<RichText>` as it is HTML */
  descriptionHTML: string;

  /** Contains strings of the closets this user has access to with this plan. Human readable/friendly */
  closetAccess: ClosetTier[];

  /** Bullet points to show for components like `<PlanCard>` */
  bulletPointCopy: string[];

  /** ID of plan as represented in Stripe */
  stripePlanId: string;
};

export type InsuranceProduct = {
  /** EPCC product ID */
  id: string;

  /** Human Readable name for the Insurance Product */
  name: string;

  /** Dev friendly name. Can be used as a className and the sort */
  slug: string;

  /** Shows monthly pricing */
  price: Price;

  /** Render this via `<RichText>` as it is HTML */
  descriptionHTML: string;

  /** ID of plan as represented in Stripe */
  stripePlanId: string;
};

export type FreezeProduct = {
  /** EPCC product ID */
  id: string;

  /** Human Readable name for the Subscription Product */
  name: string;

  /** Dev friendly name. Can be used as a className and the sort */
  slug: string;

  /** Shows monthly pricing */
  price: Price;

  /** Render this via `<RichText>` as it is HTML */
  descriptionHTML: string;

  /** ID of plan as represented in Stripe */
  stripePlanId: string;
};
